/**
 * EXPORT UTILITIES
 *
 * Functions for generating and downloading report exports (CSV, etc.)
 */

/**
 * Generate CSV content from report data
 *
 * @param {object} reportData - Report data object (project, release, or scenario level)
 * @param {string} level - Report level: 'project' | 'release' | 'scenario'
 * @returns {string} CSV content
 */
export const generateReportCSV = (reportData, level) => {
  if (!reportData || !reportData.summary) {
    return ''
  }

  const rows = []
  const summary = reportData.summary

  // Header info based on level
  rows.push(['Report Type', level.charAt(0).toUpperCase() + level.slice(1) + ' Report'])
  rows.push(['Generated At', new Date().toISOString()])
  rows.push([])

  // Level-specific header info
  if (level === 'project') {
    rows.push(['Project ID', reportData.jiraProjectId])
    rows.push(['Project Key', reportData.projectKey])
    rows.push(['Project Name', reportData.projectName])
    rows.push(['Total Releases', reportData.totalReleases])
  } else if (level === 'release') {
    rows.push(['Release ID', reportData.releaseId])
    rows.push(['Release Name', reportData.releaseName])
    rows.push(['Release Status', reportData.releaseStatus])
    rows.push(['Archived', reportData.archived ? 'Yes' : 'No'])
  } else if (level === 'scenario') {
    rows.push(['Scenario ID', reportData.scenarioId])
    rows.push(['Scenario Name', reportData.scenarioName])
    rows.push(['Priority', reportData.priority])
    if (reportData.releaseName) {
      rows.push(['Release', reportData.releaseName])
    }
  }

  rows.push([])

  // Summary metrics
  rows.push(['=== EXECUTION SUMMARY ==='])
  rows.push(['Metric', 'Value'])
  rows.push(['Total Test Cases', summary.totalTestCases])
  rows.push(['Executed Test Cases', summary.executedTestCases])
  rows.push(['Not Executed', summary.notExecutedTestCases])
  rows.push(['Pass Rate', `${summary.passRate}%`])
  rows.push(['Total Runs', summary.totalRuns])
  rows.push(['Total Scenarios', summary.totalScenarios])
  rows.push(['Total Executions', summary.totalExecutions])
  rows.push(['Last Execution Date', summary.lastExecutionDate || 'Never'])

  rows.push([])

  // Status distribution
  rows.push(['=== STATUS DISTRIBUTION ==='])
  rows.push(['Status', 'Count', 'Percentage'])

  const totalExecutions = summary.totalExecutions || 0
  if (summary.statusDistribution) {
    Object.entries(summary.statusDistribution).forEach(([status, count]) => {
      const percentage = totalExecutions > 0
        ? ((count / totalExecutions) * 100).toFixed(1)
        : '0.0'
      rows.push([status, count, `${percentage}%`])
    })
  }

  // Run breakdown for scenario level
  if (level === 'scenario' && reportData.runBreakdown && reportData.runBreakdown.length > 0) {
    rows.push([])
    rows.push(['=== TEST RUNS BREAKDOWN ==='])

    // Get all unique status names from all runs
    const allStatuses = new Set()
    reportData.runBreakdown.forEach(run => {
      Object.keys(run.statusCounts || {}).forEach(status => allStatuses.add(status))
    })
    const statusHeaders = Array.from(allStatuses)

    rows.push(['Run ID', 'Status', ...statusHeaders, 'Total'])

    reportData.runBreakdown.forEach(run => {
      const statusValues = statusHeaders.map(status => run.statusCounts?.[status] || 0)
      rows.push([
        run.runId,
        run.isCompleted ? 'Completed' : 'Active',
        ...statusValues,
        run.totalExecutions
      ])
    })
  }

  // Convert to CSV string
  return rows.map(row => {
    return row.map(cell => {
      // Handle cells that might contain commas or quotes
      const cellStr = String(cell ?? '')
      if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
        return `"${cellStr.replace(/"/g, '""')}"`
      }
      return cellStr
    }).join(',')
  }).join('\n')
}

/**
 * Generate releases summary CSV
 *
 * @param {Array} releasesSummary - Array of release summary objects
 * @param {string} projectName - Project name for header
 * @returns {string} CSV content
 */
export const generateReleasesSummaryCSV = (releasesSummary, projectName = '') => {
  if (!releasesSummary || releasesSummary.length === 0) {
    return ''
  }

  const rows = []

  // Header
  rows.push(['Releases Summary Report'])
  rows.push(['Project', projectName])
  rows.push(['Generated At', new Date().toISOString()])
  rows.push([])

  // Column headers
  rows.push([
    'Release Name',
    'Status',
    'Total Cases',
    'Executed',
    'Not Executed',
    'Pass Rate',
    'Total Runs',
    'Last Execution'
  ])

  // Data rows
  releasesSummary.forEach(release => {
    const s = release.summary
    rows.push([
      release.releaseName,
      release.releaseStatus,
      s.totalTestCases,
      s.executedTestCases,
      s.notExecutedTestCases,
      `${s.passRate}%`,
      s.totalRuns,
      s.lastExecutionDate || 'Never'
    ])
  })

  // Convert to CSV string
  return rows.map(row => {
    return row.map(cell => {
      const cellStr = String(cell ?? '')
      if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
        return `"${cellStr.replace(/"/g, '""')}"`
      }
      return cellStr
    }).join(',')
  }).join('\n')
}

/**
 * Trigger file download
 *
 * @param {string} content - File content
 * @param {string} filename - Name of the file to download
 * @param {string} mimeType - MIME type (default: 'text/csv')
 */
export const downloadFile = (content, filename, mimeType = 'text/csv;charset=utf-8;') => {
  const blob = new Blob([content], { type: mimeType })
  const url = URL.createObjectURL(blob)

  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.style.display = 'none'

  document.body.appendChild(link)
  link.click()

  // Cleanup
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

/**
 * Generate filename for export
 *
 * @param {string} level - Report level
 * @param {string} name - Entity name (project, release, or scenario name)
 * @param {string} extension - File extension (default: 'csv')
 * @returns {string} Filename
 */
export const generateFilename = (level, name, extension = 'csv') => {
  const sanitizedName = (name || 'report')
    .replace(/[^a-z0-9]/gi, '_')
    .replace(/_+/g, '_')
    .toLowerCase()

  const date = new Date().toISOString().split('T')[0]

  return `${level}_report_${sanitizedName}_${date}.${extension}`
}

/**
 * Trigger a file download from a Blob (used for server-rendered PDF / Excel
 * exports). The backend is the source of truth for the filename — pass it
 * through verbatim so the two match.
 *
 * @param {Blob} blob - Blob returned by axios (responseType: 'blob')
 * @param {string} filename
 */
export const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob)

  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.style.display = 'none'

  document.body.appendChild(link)
  link.click()

  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}
